import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconBellPlus,
  IconDeviceFloppy,
  IconExternalLink,
  IconPencil,
  IconUnlink,
  IconX,
} from "@tabler/icons-react";
import { Card } from "@/components/Card/Card";
import { ModalWrapper } from "@/components/ModalWrapper/ModalWrapper";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { FormInput } from "@/components/Inputs/FormInput";
import { OptionInput } from "@/components/Inputs/OptionInput";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useToast } from "@/components/Toast/Toast";
import { useFocusTrap } from "@/lib/a11y/useFocusTrap";
import { useRole } from "@/providers/RoleProvider";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { SUPPORTED_LOCALES, closestSupportedLocale } from "@/lib/i18n/locales";
import { getLocaleOptions } from "@/lib/utils/languages";
import { isValidEmail, normalizeEmail } from "@/lib/utils/input-validator";
import { qk } from "@/lib/query/queryKeys";
import {
  getGuestNotificationContacts,
  linkGuestNotificationContact,
  unlinkGuestNotificationContact,
} from "@/lib/api/handlers/NotificationContactHandling";
import {
  DEFAULT_NOTIFICATION_CONTACT_CAPS,
  arrivalAutomationWarning,
  contactDisplayLabel,
  contactLabelOf,
  type ArrivalAutomationWarning,
  type GuestNotificationContacts,
  type NotificationContact,
} from "@/types/NotificationContact";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import type { Guest } from "@/types/Guest";
import { DETAIL_READ_ONLY_BOX, DETAIL_READ_ONLY_TEXT } from "../../Inputs/detailFieldChrome";
import type { LayoutFieldCtx } from "./GuestLayoutFieldInput";

/**
 * Who may see and change a guest's arrival-notice recipients: OWNER / ADMIN
 * only — the backend's `authorize()` answers everyone else 403. A role not
 * known yet (empty) shows nothing (least privilege).
 */
export function canManageArrivalNotice(role: string | null | undefined): boolean {
  return role === "OWNER" || role === "ADMIN";
}

/**
 * A guest the cell is for: not archived (an archived guest cannot check in)
 * and not a request (PENDING is still a decision, DECLINED will not arrive).
 */
export function isArrivalNoticeGuest(guest: Guest, archived: boolean | undefined): boolean {
  if (archived === true || guest.archived === true) return false;
  return guest.acceptanceState !== AcceptanceState.PENDING && guest.acceptanceState !== AcceptanceState.DECLINED;
}

const active = (c: NotificationContact) => c.removedAt === null || c.removedAt === undefined;

const CHIP = "flex items-center gap-1 h-6 max-w-full min-w-0 px-2.5 rounded-full border body-3-medium";

/**
 * The main guest an inherited link comes from: the name the popup resolved
 * for its main-guest picker (`ctx.mainGuestName`), else the companion's own
 * DTO; no name ⇒ the generic wording.
 */
function mainGuestNameOf(resolved: string | undefined, guest: Guest | undefined): string | undefined {
  if (resolved) return resolved;
  const dto = guest?.mainGuestDTO;
  const name = [dto?.firstName, dto?.lastName].filter(Boolean).join(" ");
  return name || undefined;
}

/**
 * The guest popup's "Ankunft melden an" cell — plan arrival-automation Rev. 4
 * §1.8 (replaces the W4 Hop B block under the layout). Chips: the contacts
 * linked to THIS guest and the ones it inherits from its main guest
 * (read-only). "Bearbeiten" opens {@link ArrivalNoticeDialog}, which writes
 * at once (link / unlink), outside the popup's save / discard.
 *
 * A warning icon says (hover) why these contacts would hear nothing: the
 * plugin "Aktionen" is off, or no enabled workflow mails the guest field's
 * contacts — the dialog names the way out.
 *
 * Only for OWNER / ADMIN and a guest who can arrive; the popup skips the cell
 * entirely otherwise (GuestDetailLayoutView — no empty grid slot). In the
 * builder canvas (no guest, no event) it previews a static frame.
 */
export function ArrivalNoticeLayoutCell({ label, ctx }: { label: string; ctx: LayoutFieldCtx }) {
  const labelId = useId();
  const eventId = ctx.eventId ?? "";
  const guestId = ctx.guest?.guestId ?? ctx.currentGuestId ?? "";
  const live = eventId !== "" && guestId !== "" && !!ctx.guest;

  return (
    <div className="flex flex-col space-y-1.5" data-arrival-notice-field>
      {live ? (
        <ArrivalNoticeLive
          // One guest's lists — a popup that swaps the guest in place starts over.
          key={guestId}
          label={label}
          labelId={labelId}
          eventId={eventId}
          guest={ctx.guest as Guest}
          guestId={guestId}
          resolvedMainGuestName={ctx.mainGuestName}
        />
      ) : (
        <>
          <p id={labelId} className="body-3-medium truncate">
            {label}
          </p>
          <div className={DETAIL_READ_ONLY_BOX}>
            <span aria-hidden className={`${DETAIL_READ_ONLY_TEXT} font-quaternary`}>
              —
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function ArrivalNoticeLive({
  label,
  labelId,
  eventId,
  guest,
  guestId,
  resolvedMainGuestName,
}: {
  label: string;
  labelId: string;
  eventId: string;
  guest: Guest;
  guestId: string;
  resolvedMainGuestName?: string;
}) {
  const t = useTranslations("guestArrivalNotice");
  const roleCtx = useRole();
  const readOnly = !!roleCtx.isArchived || !!roleCtx.isPlanLocked;
  const [open, setOpen] = useState(false);
  // audit hop3 F4: focus goes back to "Bearbeiten" when the dialog closes —
  // also where the browser never focused the button on the click (Safari).
  const editAnchor = useRef<HTMLSpanElement | null>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) editAnchor.current?.querySelector<HTMLElement>("button")?.focus();
    wasOpen.current = open;
  }, [open]);
  const q = useQuery({
    queryKey: qk.eventSettings.guestNotificationContacts(eventId, guestId),
    queryFn: () => getGuestNotificationContacts(eventId, guestId),
    retry: false,
  });
  const data = q.data && !isServerErr(q.data) ? q.data : null;
  const own = (data?.own ?? []).filter(active);
  const inherited = (data?.inherited ?? []).filter(active);
  const warning = arrivalAutomationWarning(data);
  const mainGuestName = mainGuestNameOf(resolvedMainGuestName, guest);

  const warningText = (w: ArrivalAutomationWarning) => (w === "actionsOff" ? t("warnActionsOff") : t("warnNoPipe"));

  return (
    <>
      <div className="flex items-center gap-1.5 min-w-0">
        <p id={labelId} className="body-3-medium truncate">
          {label}
        </p>
        {warning && (
          <HoverInfo message={warningText(warning)} className="inline-flex shrink-0">
            <span data-testid={`guest-arrival-notice-warning-${warning}`} aria-label={warningText(warning)} role="img">
              <IconAlertTriangle size={14} className="hint-orange-font" aria-hidden="true" />
            </span>
          </HoverInfo>
        )}
      </div>
      <div
        className="flex flex-row flex-wrap items-center gap-1.5 min-h-9"
        aria-labelledby={labelId}
        role="group"
        data-testid="guest-arrival-notice-cell"
      >
        {q.isPending ? (
          <span className={`${DETAIL_READ_ONLY_TEXT} font-quaternary`}>…</span>
        ) : !data ? (
          // An older backend (no route) or a refused read: the dash, no edit.
          <span aria-hidden className={`${DETAIL_READ_ONLY_TEXT} font-quaternary`} data-testid="guest-arrival-notice-unavailable">
            —
          </span>
        ) : (
          <>
            {own.length === 0 && inherited.length === 0 && (
              <span className="body-3-regular font-tertiary" data-testid="guest-arrival-notice-cell-empty">
                {t("cellEmpty")}
              </span>
            )}
            {own.map((c) =>
              c.verifiedAt ? (
                <span
                  key={`o-${c.eventNotificationContactId}`}
                  className={`${CHIP} outline-quaternary surface-secondary font-primary`}
                  data-testid="guest-arrival-notice-chip"
                >
                  <span className="truncate">{contactDisplayLabel(c)}</span>
                </span>
              ) : (
                // audit hop3 F10: the pending hint as a HoverInfo (also on
                // keyboard focus), not a native `title`; said to a screen
                // reader in the chip itself.
                <HoverInfo
                  key={`o-${c.eventNotificationContactId}`}
                  message={t("chipPending")}
                  className="inline-flex max-w-full min-w-0"
                >
                  <span
                    tabIndex={0}
                    className={`${CHIP} hint-orange-outline hint-orange-bg hint-orange-font`}
                    data-testid="guest-arrival-notice-chip"
                    data-pending="true"
                  >
                    <span className="truncate">{contactDisplayLabel(c)}</span>
                    <span className="sr-only">{t("chipPending")}</span>
                  </span>
                </HoverInfo>
              ),
            )}
            {inherited.map((c) => (
              <span
                key={`i-${c.eventNotificationContactId}`}
                className={`${CHIP} outline-quaternary font-secondary`}
                data-testid="guest-arrival-notice-chip-inherited"
              >
                <span className="truncate">
                  {contactDisplayLabel(c)} ·{" "}
                  {mainGuestName ? t("inheritedVia", { name: mainGuestName }) : t("inheritedViaMainGuest")}
                </span>
              </span>
            ))}
            {!readOnly && (
              <span ref={editAnchor} className="contents">
                <SecondaryButton
                  size="xsmall"
                  width="w-max"
                  icon={IconPencil}
                  text={t("edit")}
                  ariaLabel={t("editAria", { label })}
                  testId="guest-arrival-notice-edit"
                  onClick={() => setOpen(true)}
                />
              </span>
            )}
          </>
        )}
      </div>
      {open && data && (
        <ArrivalNoticeDialog
          eventId={eventId}
          guestId={guestId}
          data={data}
          mainGuestName={mainGuestName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * The cell's dialog: the guest's own links (unlink per row), the inherited
 * ones (read-only), and "Kontakt verknüpfen" — an address, an optional label
 * and a language; the backend finds or creates the contact (double opt-in)
 * and links the guest. Every action writes AT ONCE — the popup's
 * save / discard never touches the links. Links in it open in a new tab, so
 * the popup and its unsaved edits stay.
 */
export function ArrivalNoticeDialog({
  eventId,
  guestId,
  data,
  mainGuestName,
  onClose,
}: {
  eventId: string;
  guestId: string;
  data: GuestNotificationContacts;
  mainGuestName?: string;
  onClose: () => void;
}) {
  const t = useTranslations("guestArrivalNotice");
  const tPage = useTranslations("eventSettingsNotifications");
  const tErrors = useTranslations("errors");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const titleId = useId();

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [language, setLanguage] = useState(() => closestSupportedLocale(locale));
  const [busy, setBusy] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [privacyMissing, setPrivacyMissing] = useState(false);

  // audit hop3 F4 / F11: a dialog for assistive tech (role, name, focus kept
  // inside, Escape), held shut while a link or an unlink is on its way — the
  // write completes either way, and closing would hide its outcome — and the
  // backdrop does not throw away a half-typed address.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelReady, setPanelReady] = useState(false);
  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelReady(node !== null);
  }, []);
  useFocusTrap(panelRef, panelReady, undefined, { cycleTab: true });
  const inFlight = busy || unlinkingId !== null;
  const formDirty = adding && (email.trim() !== "" || label.trim() !== "");
  const close = useCallback(() => {
    if (!inFlight) onClose();
  }, [inFlight, onClose]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const panel = panelRef.current;
      // An open list inside (the language picker) owns its Escape — same
      // contract as Modal / PDeleteConfirmation. The panel itself is marked
      // too (so a host Modal stands down), hence "another" owner.
      const target = e.target;
      if (target instanceof Element) {
        const owner = target.closest('[data-escape-owner="true"]');
        if (owner && owner !== panel) return;
      }
      if (panel?.querySelector('[data-escape-owner="true"]')) return;
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [close]);

  const languageOptions = useMemo(() => getLocaleOptions(SUPPORTED_LOCALES, locale), [locale]);
  const languageLabels = useMemo(
    () => Object.fromEntries(languageOptions.map((o) => [o.code, o.name])),
    [languageOptions],
  );

  const own = (data.own ?? []).filter(active);
  const inherited = (data.inherited ?? []).filter(active);
  const warning = arrivalAutomationWarning(data);
  const perGuestCap = DEFAULT_NOTIFICATION_CONTACT_CAPS.contactsPerGuest;
  const atPerGuestCap = own.length >= perGuestCap;
  const settingsPath = `/event/${encodeURIComponent(eventId)}/settings/notifications`;

  const refresh = async () => {
    // The guest's lists, the settings page's list and the automation picker.
    await queryClient.invalidateQueries({ queryKey: qk.eventSettings.notificationsPrefix(eventId) });
  };

  const closeForm = () => {
    setAdding(false);
    setEmail("");
    setLabel("");
    setFormError(null);
  };

  const submit = async () => {
    if (busy) return;
    const address = normalizeEmail(email);
    if (!isValidEmail(address)) {
      setFormError(tErrors("invalidEmail"));
      return;
    }
    setBusy(true);
    setFormError(null);
    setPrivacyMissing(false);
    try {
      const result = await linkGuestNotificationContact(eventId, guestId, {
        email: address,
        label: label.trim(),
        language,
      });
      if (isServerErr(result)) {
        if (result.message === "organiserPrivacyUrlMissing") {
          setPrivacyMissing(true);
          return;
        }
        setFormError(translateBackendError(tErrors, result));
        return;
      }
      showToast(t("linked"), "success");
      closeForm();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (contact: NotificationContact) => {
    if (unlinkingId !== null) return;
    setUnlinkingId(contact.eventNotificationContactId);
    setUnlinkError(null);
    try {
      const result = await unlinkGuestNotificationContact(eventId, contact.eventNotificationContactId, guestId);
      if (isServerErr(result)) {
        setUnlinkError(translateBackendError(tErrors, result));
        return;
      }
      showToast(t("unlinked"), "success");
      await refresh();
    } finally {
      setUnlinkingId(null);
    }
  };

  const row = (contact: NotificationContact, inheritedRow: boolean) => {
    const named = contactLabelOf(contact) !== "";
    const displayLabel = contactDisplayLabel(contact);
    return (
      <li
        key={`${inheritedRow ? "i" : "o"}-${contact.eventNotificationContactId}`}
        className="flex flex-row flex-wrap items-center justify-between gap-2 rounded-lg border outline-tertiary px-3 py-2"
        data-testid={inheritedRow ? "guest-arrival-notice-inherited-row" : "guest-arrival-notice-own-row"}
      >
        <div className="flex flex-col min-w-0">
          <span className="body-2-medium break-words" data-testid="guest-arrival-notice-row-title">
            {displayLabel}
          </span>
          {named && <span className="body-3-regular font-secondary break-all">{contact.email}</span>}
          <span className="flex flex-row flex-wrap gap-1.5 mt-1">
            {inheritedRow && (
              <span
                className="px-2 py-0.5 rounded-full body-4-regular surface-quaternary font-primary"
                data-testid="guest-arrival-notice-via"
              >
                {mainGuestName ? t("inheritedVia", { name: mainGuestName }) : t("inheritedViaMainGuest")}
              </span>
            )}
            {!contact.verifiedAt && (
              <span className="px-2 py-0.5 rounded-full body-4-regular hint-orange-bg hint-orange-font">
                {tPage("pendingChip")}
              </span>
            )}
          </span>
        </div>
        {!inheritedRow && (
          <SecondaryButton
            size="xsmall"
            width="w-max"
            icon={IconUnlink}
            text={t("unlink")}
            ariaLabel={t("unlinkAria", { label: displayLabel })}
            testId="guest-arrival-notice-unlink"
            loading={unlinkingId === contact.eventNotificationContactId}
            disabled={unlinkingId !== null}
            onClick={() => void unlink(contact)}
          />
        )}
      </li>
    );
  };

  return (
    // audit hop3 F3: above the guest popup wherever it opens — the send
    // dialog's opt-in list raises it to z-[10070]. Its hints (the per-guest
    // cap on "Kontakt verknüpfen") paint one above the rung: ModalWrapper
    // hands them the layer (audit hop3 r2 M1).
    <ModalWrapper
      onClose={close}
      animation="scale"
      zIndex="overGuestPopup"
      size="max-w-160"
      mobile="sheet"
      backdropClosable={!inFlight && !formDirty}
    >
      <div
        ref={attachPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-escape-owner="true"
        tabIndex={-1}
        className="outline-none"
        data-testid="guest-arrival-notice-dialog-panel"
      >
      <Card
        width="w-full sm:w-[95vw] sm:max-w-160"
        rounded="rounded-t-3xl sm:rounded-3xl"
        height="max-h-[90dvh]"
        className="overflow-y-auto"
        padding="p-6"
        customHeader={
          <h2 id={titleId} className="h2">
            {t("dialogTitle")}
          </h2>
        }
        icon={IconX}
        iconActionOnClick={close}
        iconAriaLabel={tErrors("close")}
        // audit hop3 r2 L9: held while a link / unlink is on its way — says
        // so instead of looking clickable and doing nothing.
        iconDisabled={inFlight}
        iconBusy={inFlight}
        iconTestId="guest-arrival-notice-dialog-close"
      >
        <div className="flex flex-col gap-3 mt-4" data-testid="guest-arrival-notice-dialog">
          {warning && (
            <div
              className="flex flex-row flex-wrap items-center gap-2 rounded-lg border px-3 py-2 hint-orange-bg hint-orange-outline"
              data-testid={`guest-arrival-notice-dialog-warning-${warning}`}
            >
              <IconAlertTriangle size={16} className="shrink-0 hint-orange-font" aria-hidden="true" />
              <span className="body-3-regular hint-orange-font">
                {warning === "actionsOff" ? t("warnActionsOff") : t("warnNoPipe")}
              </span>
              <a
                href={
                  warning === "actionsOff"
                    ? `/event/${encodeURIComponent(eventId)}/settings/plugins`
                    : `/event/${encodeURIComponent(eventId)}/automation/workflows?create=arrival`
                }
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 body-3-medium hint-orange-font underline"
                data-testid="guest-arrival-notice-warning-link"
              >
                {warning === "actionsOff" ? t("warnActionsOffLink") : t("warnNoPipeLink")}
                <IconExternalLink size={12} aria-hidden="true" />
              </a>
            </div>
          )}

          <div className="flex flex-row flex-wrap items-center justify-between gap-2">
            <a
              href={settingsPath}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 body-3-medium font-secondary hover:opacity-80"
              data-testid="guest-arrival-notice-manage"
            >
              {t("manage")}
              <IconExternalLink size={12} aria-hidden="true" />
            </a>
            {!adding && (
              <SecondaryButton
                size="small"
                width="w-max"
                icon={IconBellPlus}
                text={t("add")}
                disabled={atPerGuestCap}
                disabledMessage={atPerGuestCap ? t("perGuestCap", { max: perGuestCap }) : ""}
                testId="guest-arrival-notice-add"
                onClick={() => {
                  setPrivacyMissing(false);
                  setFormError(null);
                  setUnlinkError(null);
                  setAdding(true);
                }}
              />
            )}
          </div>

          {own.length > 0 ? (
            <ul className="flex flex-col gap-2" data-testid="guest-arrival-notice-own">
              {own.map((c) => row(c, false))}
            </ul>
          ) : (
            <p className="body-3-regular font-tertiary" data-testid="guest-arrival-notice-own-empty">
              {t("ownEmpty")}
            </p>
          )}

          {inherited.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid="guest-arrival-notice-inherited">
              {inherited.map((c) => row(c, true))}
            </ul>
          )}

          {unlinkError && (
            <p className="body-3-regular font-error" role="alert" data-testid="guest-arrival-notice-unlink-error">
              {unlinkError}
            </p>
          )}

          {privacyMissing && (
            <div
              role="alert"
              className="flex flex-col gap-1 rounded-lg border px-3 py-2 hint-orange-bg hint-orange-outline"
              data-testid="guest-arrival-notice-privacy-missing"
            >
              <p className="body-3-regular hint-orange-font">{t("privacyMissing")}</p>
              <a
                href={settingsPath}
                target="_blank"
                rel="noreferrer"
                className="body-3-medium underline hint-orange-font"
                data-testid="guest-arrival-notice-settings-link"
              >
                {t("privacyMissingLink")}
              </a>
            </div>
          )}

          {adding && (
            <form
              // Our own address check speaks under the field (the browser's
              // bubble would not). The label is optional.
              noValidate
              className="flex flex-col gap-3"
              data-testid="guest-arrival-notice-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormInput
                  id="guest-arrival-notice-email"
                  type="email"
                  label={tPage("emailLabel")}
                  placeholder={tPage("emailPlaceholder")}
                  value={email}
                  onChangeAction={(value) => {
                    setEmail(value);
                    setFormError(null);
                  }}
                  data-testid="guest-arrival-notice-email"
                />
                <FormInput
                  id="guest-arrival-notice-label"
                  label={tPage("labelLabel")}
                  placeholder={tPage("labelPlaceholder")}
                  value={label}
                  maxLength={80}
                  onChangeAction={setLabel}
                  data-testid="guest-arrival-notice-label"
                />
              </div>
              <OptionInput
                id="guest-arrival-notice-language"
                label={t("languageLabel")}
                options={languageOptions.map((o) => o.code)}
                optionLabels={languageLabels}
                defaultValue={language}
                required
                strictOption
                setParent={setLanguage}
                testId="guest-arrival-notice-language"
              />
              {formError && (
                <p className="body-3-regular font-error" role="alert" data-testid="guest-arrival-notice-error">
                  {formError}
                </p>
              )}
              <div className="flex flex-row flex-wrap gap-2 justify-end">
                <SecondaryButton size="small" width="w-max" icon={IconX} text={tPage("cancel")} onClick={closeForm} />
                <PrimaryButton
                  size="small"
                  width="w-max"
                  icon={IconDeviceFloppy}
                  text={tPage("save")}
                  type="submit"
                  loading={busy}
                  disabled={busy || email.trim() === ""}
                  testId="guest-arrival-notice-save"
                />
              </div>
            </form>
          )}
        </div>
      </Card>
      </div>
    </ModalWrapper>
  );
}
